import {
  RealtimeChannel,
  RealtimePostgresChangesFilter,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from "@supabase/supabase-js";
import Debug from "debug";
import PQueue from "p-queue";
import { wireSupabaseRealtimeAuth } from "./wireSupabaseRealtimeAuth";

const debug = Debug("oom:SupabaseSubscriptionManager");

export type SupabaseEventHandler = (key: string, payload: RealtimePostgresChangesPayload<any>) => void;

/**
 * Manages the actual Supabase Realtime channel and subscriptions.
 */
export class SupabaseSubscriptionManager {
  private channelId: number = 0;
  private channel: RealtimeChannel | undefined;
  private _subscribeQueue = new PQueue({ concurrency: 1 });
  // Renamed from currentFilters and made private state
  private activeSubscriptions: Map<string, RealtimePostgresChangesFilter<any>> = new Map();
  private isResubscribeQueued = false; // Flag to prevent redundant queue additions

  constructor(
    private supabase: SupabaseClient,
    private onSupabaseEvent: SupabaseEventHandler,
    private name: string = "subscription_manager",
  ) {
    wireSupabaseRealtimeAuth(supabase);
  }

  /**
   * Adds a specific filter to the list of active Supabase subscriptions
   * and triggers a channel update.
   * @param key A unique key identifying the subscription filter.
   * @param filter The Supabase filter definition.
   */
  addSubscription(key: string, filter: RealtimePostgresChangesFilter<any>) {
    if (!this.supabase) {
      debug("Supabase client not available, cannot add subscription.");
      return;
    }
    if (this.activeSubscriptions.has(key)) {
      debug(`Subscription key ${key} already exists. Ignoring add.`);
      // Optionally update the filter if needed: this.activeSubscriptions.set(key, filter);
      return;
    }
    debug(`Adding subscription key: ${key}`, filter);
    this.activeSubscriptions.set(key, filter);
    this._queueResubscribe();
  }

  /**
   * Removes a specific filter from the list of active Supabase subscriptions
   * and triggers a channel update.
   * @param key The unique key identifying the subscription filter to remove.
   */
  removeSubscription(key: string) {
    if (!this.supabase) {
      debug("Supabase client not available, cannot remove subscription.");
      return;
    }
    if (!this.activeSubscriptions.has(key)) {
      debug(`Subscription key ${key} not found. Ignoring remove.`);
      return;
    }
    debug(`Removing subscription key: ${key}`);
    this.activeSubscriptions.delete(key);
    this._queueResubscribe();
  }

  /**
   * Queues the channel resubscription logic if not already queued.
   * This debounces multiple add/remove calls into a single channel update.
   */
  private _queueResubscribe() {
    if (this.isResubscribeQueued) {
      debug("Resubscribe already queued. Skipping.");
      return;
    }
    this.isResubscribeQueued = true;

    this._subscribeQueue.add(async () => {
      // Reset the flag when the task starts executing
      this.isResubscribeQueued = false;
      await this._resubscribeAll();
    });
  }

  /**
   * Establishes or updates the Supabase Realtime subscription based on the current activeSubscriptions.
   * This method ensures that only one subscription update happens at a time.
   * It uses the internal `activeSubscriptions` map.
   */
  private async _resubscribeAll() {
    debug(`Resubscribing to Supabase channel: ${this.name}`);

    // Create a snapshot of the filters at the start of this execution
    const filtersToApply = new Map(this.activeSubscriptions);

    try {
      const nextChannelId = this.channelId + 1;
      const nextChannelName = `${this.name}:${nextChannelId}`;
      const nextChannel = this.supabase.channel(nextChannelName);
      // Update the current channel reference and ID
      this.channelId = nextChannelId;
      this.channel = nextChannel;

      // Optional: Add error handling for system messages if needed
      // @ts-ignore - Accessing private/internal method for enhanced logging
      const oldOnMessage = nextChannel._onMessage;
      // @ts-ignore
      nextChannel._onMessage = (_event: string, payload: any, _ref?: string) => {
        if (_event === "system" && payload?.status === "error") {
          console.error(`[${nextChannelName}] System error payload:`, payload);
          debug(`System error on channel ${nextChannelName}`, payload);
        }
        // Call original handler
        return oldOnMessage.call(nextChannel, _event, payload, nextChannelId);
      };

      const filterCount = filtersToApply.size;
      debug(`Setting up channel ${nextChannelName} with ${filterCount} filters.`);

      // If there are no filters, unsubscribe the current channel if it exists and return.
      if (filterCount === 0) {
        if (this.channel) {
          debug(`No filters provided. Unsubscribing channel ${this.channel.topic}`);
          await this.unsubscribeCurrentChannel(); // Use specific method
        } else {
          debug("No filters provided and no active channel.");
        }
        return;
      }

      // Register event listeners for each filter in the snapshot
      for (const [key, filter] of filtersToApply) {
        debug(`Adding listener for key: ${key}`, filter);
        nextChannel.on("postgres_changes", filter, (payload) => {
          // Only forward if the subscription still exists when the event arrives
          if (this.activeSubscriptions.has(key)) {
            debug("postgres_changes", key, payload.eventType, payload.table, payload);
            this.onSupabaseEvent(key, payload);
          } else {
            debug(`Received event for removed key ${key}. Ignoring.`);
          }
        });
      }

      // Subscribe to the channel
      await new Promise<void>((resolve, reject) => {
        debug(`[${nextChannelName}] Subscribing to channel`);
        nextChannel.subscribe((status, err) => {
          if (err) {
            console.error(`[${nextChannelName}] Subscription error:`, err);
            debug(`Error subscribing to channel ${nextChannelName}`, err);
            reject(err);
            return;
          }

          if (status === "SUBSCRIBED") {
            debug(`Successfully subscribed to channel ${nextChannelName} with status: ${status}`);
            resolve();
          } else {
            debug(`Channel ${nextChannelName} status update: ${status}`);
            // Potentially handle other statuses like 'CHANNEL_ERROR', 'TIMED_OUT' if needed
          }
        });
      });

      debug(`Channel updated to ${this.channel.topic}`);
    } catch (e) {
      console.error(`[${this.name}] Error during subscription process:`, e);
      debug("Error in subscribe queue execution", e);
      this._queueResubscribe();

      // Consider retry logic or error propagation if necessary
    } finally {
      // Only prune stale postgres_changes channels owned by this manager. Other Realtime
      // channels (e.g. account broadcast) must not be removed here.
      const ownedPrefix = `realtime:${this.name}:`;
      for (const channel of this.supabase.getChannels()) {
        if (channel === this.channel) continue;
        const topic = channel.topic ?? "";
        if (topic.startsWith(ownedPrefix)) {
          await this.supabase.removeChannel(channel);
        }
      }
    }
  }

  /**
   * Unsubscribes from the current Supabase channel specifically.
   */
  private async unsubscribeCurrentChannel() {
    const currentChannel = this.channel;
    if (!currentChannel) {
      debug("No active channel to unsubscribe from.");
      return;
    }

    debug(`Unsubscribing from channel ${currentChannel.topic}`);
    try {
      await currentChannel.unsubscribe();
      debug(`Successfully unsubscribed from ${currentChannel.topic}`);
      // Optionally remove the specific channel
      // await this.supabase.removeChannel(currentChannel);
    } catch (error) {
      console.error(`Error unsubscribing from channel ${currentChannel.topic}:`, error);
      debug(`Error unsubscribing from channel ${currentChannel.topic}`, error);
    } finally {
      if (this.channel === currentChannel) {
        this.channel = undefined;
      }
    }
  }

  /**
   * Clears the queue, unsubscribes the current channel, and removes all channels managed by the client.
   * Should be called on cleanup (e.g., window unload).
   */
  async unsubscribe() {
    // Clear the queue to prevent pending subscriptions from running
    this._subscribeQueue.clear();
    this.isResubscribeQueued = false; // Reset queue flag

    await this.unsubscribeCurrentChannel(); // Unsubscribe the current one first

    // Reset internal state
    this.activeSubscriptions.clear();

    // Remove all channels associated with this Supabase client instance
    // Be cautious if other parts of the app might use Supabase channels independently
    try {
      const removedChannels = await this.supabase.removeAllChannels();
      debug(`Removed all Supabase channels. Count: ${removedChannels?.length ?? 0}`);
    } catch (error) {
      console.error("Error removing all channels:", error);
      debug("Error removing all channels", error);
    }
  }
}
